'use client';

import type * as React from 'react';
import type { Context } from 'react';
import { createContext, use } from 'react';

import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Variant types
// ---------------------------------------------------------------------------

export type PanelDensity = 'default' | 'compact' | 'flush';
export type PanelElevation = 'flat' | 'elevated';
export type PanelSurface = 'solid' | 'dashed' | 'none';
export type PanelTone = 'default' | 'accent' | 'brand';

// ---------------------------------------------------------------------------
// Token tables (module-level — React Compiler friendly)
// ---------------------------------------------------------------------------

const ROOT_FRAME = 'flex flex-col text-foreground-primary rounded-card' as const;
const ROOT_BG_SOLID = 'bg-background-primary';

const ROOT_DENSITY: Record<PanelDensity, string> = {
  default: 'gap-6 py-4',
  compact: 'gap-3 py-4',
  flush: 'gap-0 py-0',
};

const ROOT_ELEVATION: Record<PanelElevation, string> = {
  flat: '',
  elevated: 'shadow-elevation-sticky backdrop-blur-sm',
};

const ROOT_SURFACE: Record<PanelSurface, string> = {
  // Solid panels rest FLAT — hairline border only, identical in light and dark.
  // The elevation system is reserved for floating surfaces + interactive
  // hover-lift, not a resting shadow on content panels.
  solid:
    'border-solid border-border-primary [border-width:var(--border-width-hairline)] dark:border-border-primary',
  dashed: 'border-2 border-dashed border-border-primary',
  none: '',
};

const ROOT_TONE: Record<PanelTone, string> = {
  default: '',
  // Accent — primary-tinted promotional / highlight surface used for referral
  // banners, rank cards, creator skeletons. Overrides border + background
  // for the surface-coloring; pairs with any density / surface choice.
  accent: 'border-border-primary/20 bg-foreground-primary/5',
  // Brand — brand-bordered "live / highlighted" surface (pending TokenCta +
  // graduating banner). Theme-aware border: light = the soft
  // `border-border-brand` (brand-200), dark = the vivid `border-border-brand-strong`
  // (brand-500; the plain `border-brand` is too dark in dark mode). Plus
  // `shadow-panel-brand-glow` — a 3px inset brand inner-glow "Ring" (light:
  // brand-200 @ 18%, dark: brand-400 @ 34%). The `dark:` variants are required
  // to beat the solid surface's `dark:border-border-primary`.
  brand:
    'border-border-brand dark:border-border-brand-strong shadow-panel-brand-glow dark:shadow-panel-brand-glow',
};

const HEADER_DENSITY: Record<PanelDensity, string> = {
  default:
    '@container/panel-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-4 has-data-[slot=panel-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
  compact:
    '@container/panel-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-4 pb-0 has-data-[slot=panel-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
  flush: 'px-0 pb-0',
};

const CONTENT_DENSITY: Record<PanelDensity, string> = {
  default: 'px-4',
  compact: 'px-4 pt-0',
  flush: 'px-0 pt-0',
};

const FOOTER_DENSITY: Record<PanelDensity, string> = {
  default: 'flex items-center px-4 [.border-t]:pt-6',
  compact: 'flex items-center px-4 pt-0 [.border-t]:pt-6',
  flush: 'flex items-center px-0 pt-0',
};

// ---------------------------------------------------------------------------
// Density context — Panel writes, sub-components read
// ---------------------------------------------------------------------------

const PanelDensityContext: Context<PanelDensity> = createContext<PanelDensity>('default');

function usePanelDensity(): PanelDensity {
  return use(PanelDensityContext);
}

// ---------------------------------------------------------------------------
// Panel root
// ---------------------------------------------------------------------------

export interface PanelProps extends React.ComponentProps<'div'> {
  density?: PanelDensity | undefined;
  elevation?: PanelElevation | undefined;
  surface?: PanelSurface | undefined;
  tone?: PanelTone | undefined;
  /** Omit `bg-background-primary` so the panel inherits the parent surface (e.g. page secondary). */
  inheritBackground?: boolean | undefined;
}

/**
 * Panel — design-system surface primitive (replaces direct shadcn Card usage).
 *
 * **className discipline.** `className` IS accepted on the root for positioning
 * and layout passthrough (width, overflow, hover transitions, max-width etc).
 * It MUST NOT be used to override visual design tokens — radius, border, padding,
 * gap, surface color, shadow — those flow through the variant props
 * (`density`, `elevation`, `surface`, `inheritBackground`). FQ-110 catches hand-rolled card-like
 * `<div>`s; code review catches Panel root `className` that smuggles in tokens.
 */
export function Panel({
  density = 'default',
  elevation = 'flat',
  surface = 'solid',
  tone = 'default',
  inheritBackground = false,
  className,
  children,
  ...props
}: PanelProps): React.ReactNode {
  return (
    <PanelDensityContext.Provider value={density}>
      <div
        data-slot="panel"
        data-density={density}
        data-elevation={elevation}
        data-surface={surface}
        data-tone={tone}
        data-background={inheritBackground ? 'inherit' : 'solid'}
        className={cn(
          ROOT_FRAME,
          !inheritBackground && ROOT_BG_SOLID,
          ROOT_DENSITY[density],
          ROOT_ELEVATION[elevation],
          ROOT_SURFACE[surface],
          ROOT_TONE[tone],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </PanelDensityContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — className is layout-passthrough only
// ---------------------------------------------------------------------------

export type PanelSubProps = React.HTMLAttributes<HTMLDivElement>;

export function PanelHeader({ className, ...props }: PanelSubProps): React.ReactNode {
  const density = usePanelDensity();
  return (
    <div data-slot="panel-header" className={cn(HEADER_DENSITY[density], className)} {...props} />
  );
}

export function PanelContent({ className, ...props }: PanelSubProps): React.ReactNode {
  const density = usePanelDensity();
  return (
    <div data-slot="panel-content" className={cn(CONTENT_DENSITY[density], className)} {...props} />
  );
}

export function PanelFooter({ className, ...props }: PanelSubProps): React.ReactNode {
  const density = usePanelDensity();
  return (
    <div data-slot="panel-footer" className={cn(FOOTER_DENSITY[density], className)} {...props} />
  );
}

export function PanelTitle({ className, ...props }: PanelSubProps): React.ReactNode {
  return (
    <div data-slot="panel-title" className={cn('leading-none font-medium', className)} {...props} />
  );
}

export function PanelDescription({ className, ...props }: PanelSubProps): React.ReactNode {
  return (
    <div
      data-slot="panel-description"
      className={cn('text-body text-foreground-secondary', className)}
      {...props}
    />
  );
}

export function PanelAction({ className, ...props }: PanelSubProps): React.ReactNode {
  return (
    <div
      data-slot="panel-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}
